import { Box, LiveRegion, Text, VisuallyHidden } from '../../../mads';
import { useSuggestionsAnnouncement } from '../../hooks/useSuggestionsAnnouncement';

import { Platform, StyleSheet } from 'react-native';

export interface SuggestionsAnnouncementProps {
    /**
     * Current suggestions list. The component reads only `.length`. Pass an
     * empty array to suppress announcements (e.g. while the user is
     * mid-selection or the input is unfocused).
     */
    suggestions: unknown[];
}

/**
 * Renders a screen-reader-only live region that announces suggestion list
 * availability (WCAG 4.1.3). Mount it anywhere in the tree where you want
 * the announcement to land — most importantly, OUTSIDE any RN `<Modal>`
 * portal on mobile web so SRs reliably observe the aria-live mutation.
 *
 * Gating semantics live in `useSuggestionsAnnouncement`: announce once per
 * 0 -> N appearance, hold while count fluctuates, reset on N -> 0.
 *
 * Platform-branched child rendering:
 * - On web, `<VisuallyHidden>` keeps the announcement text in the DOM so
 *   screen readers observe the `aria-live` mutation.
 * - On native, mads `<LiveRegion>` imperatively announces (extracting the
 *   string from the JSX child via `extractText()`), but it also renders
 *   {children}; mads `<VisuallyHidden>` hard-codes
 *   `accessibilityElementsHidden={false}`, so it stays in the VoiceOver /
 *   TalkBack swipe order. To avoid creating a phantom swipe target, we
 *   render an SR-hidden `<View>` carrier instead. The `<Text>` child is
 *   required so `extractText()` can pull the string for the imperative
 *   announce.
 */
export function SuggestionsAnnouncement({ suggestions }: SuggestionsAnnouncementProps) {
    const announcement = useSuggestionsAnnouncement(suggestions);
    return (
        <LiveRegion>
            {Platform.OS === 'web' ? (
                <VisuallyHidden>{announcement}</VisuallyHidden>
            ) : (
                <Box
                    accessible={false}
                    accessibilityElementsHidden
                    importantForAccessibility="no-hide-descendants"
                    sx={styles.srHiddenCarrier}
                >
                    <Text>{announcement}</Text>
                </Box>
            )}
        </LiveRegion>
    );
}

const styles = StyleSheet.create({
    srHiddenCarrier: {
        position: 'absolute',
        height: 0,
        width: 0,
        opacity: 0,
        overflow: 'hidden',
    },
});
